/**
 * API Client for MarryPlan Backend
 * Communicates with Vercel serverless functions
 */

const API_BASE = '/api';

interface ApiResponse<T> {
  data: T | null;
  error: string | null;
}

class ApiClient {
  private authToken: string | null = null;

  setAuthToken(token: string | null) {
    this.authToken = token;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (this.authToken) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.authToken}`;
    }

    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        headers,
      });

      let data: any;
      const contentType = response.headers.get('content-type');
      try {
        data = contentType?.includes('application/json')
          ? await response.json()
          : { error: await response.text() || `Chyba ${response.status}` };
      } catch (parseErr: any) {
        return { data: null, error: response.ok ? 'Neplatná odpověď serveru' : `Chyba ${response.status}` };
      }

      if (!response.ok) {
        const msg = typeof data?.error === 'string' ? data.error : 'Request failed';
        return { data: null, error: msg };
      }

      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err.message || 'Chyba připojení' };
    }
  }

  // Health check
  async health() {
    return this.request('/health');
  }

  // Auth
  async getCurrentUser() {
    return this.request('/auth/user');
  }

  async updateProfile(updates: Record<string, any>) {
    return this.request('/auth/user', {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Weddings
  async getWeddings() {
    return this.request('/weddings');
  }

  async getWedding(id: string) {
    return this.request(`/weddings/${id}`);
  }

  async createWedding(data: { title: string; date: string; location: string; default_language?: string }) {
    return this.request('/weddings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWedding(id: string, updates: Record<string, any>) {
    return this.request(`/weddings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteWedding(id: string) {
    return this.request(`/weddings/${id}`, {
      method: 'DELETE',
    });
  }

  // Guests (returns only visible_in_guests = true; bride/groom are in wedding_team)
  async getGuests(weddingId: string) {
    return this.request(`/weddings/${weddingId}/guests`);
  }

  async createGuest(weddingId: string, data: Record<string, any>) {
    return this.request(`/weddings/${weddingId}/guests`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Wedding team (bride, groom, coordinator)
  async getWeddingTeam(weddingId: string) {
    return this.request<{ bride: any; groom: any; coordinator: any }>(`/weddings/${weddingId}/wedding_team`);
  }

  async upsertWeddingTeamMember(
    weddingId: string,
    slot: 'bride' | 'groom' | 'coordinator',
    data: { full_name: string; contact_email?: string; contact_phone?: string; allergies?: string; notes?: string }
  ) {
    return this.request(`/weddings/${weddingId}/wedding_team/${slot}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Tasks
  async getTasks(weddingId: string) {
    return this.request(`/weddings/${weddingId}/tasks`);
  }

  async createTask(weddingId: string, data: Record<string, any>) {
    return this.request(`/weddings/${weddingId}/tasks`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Budget
  async getBudget(weddingId: string) {
    return this.request(`/weddings/${weddingId}/budget`);
  }

  async createBudgetItem(weddingId: string, data: Record<string, any>) {
    return this.request(`/weddings/${weddingId}/budget`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Vendors
  async getVendors(weddingId: string) {
    return this.request(`/weddings/${weddingId}/vendors`);
  }

  async createVendor(weddingId: string, data: Record<string, any>) {
    return this.request(`/weddings/${weddingId}/vendors`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Schedule
  async getSchedule(weddingId: string) {
    return this.request(`/weddings/${weddingId}/schedule`);
  }

  async createScheduleItem(weddingId: string, data: Record<string, any>) {
    return this.request(`/weddings/${weddingId}/schedule`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Tables (Seating)
  async getTables(weddingId: string) {
    return this.request(`/weddings/${weddingId}/tables`);
  }

  async createTable(weddingId: string, data: { name: string; capacity?: number }) {
    return this.request(`/weddings/${weddingId}/tables`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Public wedding page (no auth required)
  async getPublicWedding(slug: string) {
    return this.request(`/public/${slug}`);
  }

  // Admin: Create user (optional initial password; if omitted, one is generated)
  async createUser(data: {
    username: string;
    email?: string;
    password?: string;
    wedding_id: string;
    role_key?: string;  // New: WEDDING_ADMIN, COORDINATOR, BRIDE, GROOM, WITNESS, BRIDESMAID, FAMILY_GUEST
    wedding_role?: string;  // Legacy
    is_wedding_admin?: boolean;  // Legacy
  }) {
    return this.request('/auth/create-user', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Password reset (email-based; not used when everything is in-app)
  async resetPassword(username: string) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ username }),
    });
  }

  // Change own password (current + new; no email, no OTP)
  async changePassword(current_password: string, new_password: string) {
    return this.request<{ message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password, new_password }),
    });
  }

  // Admin: set new password for another user (no current password)
  async adminResetPassword(userId: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/admin-reset-password', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, new_password: newPassword }),
    });
  }
}

export const apiClient = new ApiClient();
export default apiClient;
